/*
 *   File : DynamicFilter.jsx
 *   Author : https://evoqins.com
 *   Description : Dynamic reusable filter component
 *   Version : 1.0.0
 */

import React, { useState } from "react";
import Icon from "../icons";
import { CheckboxSelect } from "../DropDown";
import { DateRangePicker } from "../Calender";
import { PrimaryButton } from "../Cta";

export default function Filter({
  filters = {}, // backend filter data
  selectedFilters,
  setSelectedFilters,
  dateLabelName,
  showDateRange = true,
}) {
  const [open, setOpen] = useState(false);
  const [analyticsDateRange, setAnalyticsDateRange] = useState([
    new Date(),
    new Date(),
  ]);

  // Handle dropdown changes
  const handleSelectChangeFor = (key, selected) => {
    if (!selected) {
      setSelectedFilters((prev) => ({ ...prev, [key.toLowerCase()]: null }));
      return;
    }

    if (!Array.isArray(selected)) {
      setSelectedFilters((prev) => ({ ...prev, [key.toLowerCase()]: selected.value }));
      return;
    }

    const normalized = selected.map((s) =>
      s?.value !== undefined ? s.value : s
    );
    setSelectedFilters((prev) => ({ ...prev, [key.toLowerCase()]: normalized }));
  };

  // Handle Date Range
  const formatDate = (date) => {
    if (!date) return "";
    const d = new Date(date);
    return `${String(d.getDate()).padStart(2, "0")}-${String(
      d.getMonth() + 1
    ).padStart(2, "0")}-${d.getFullYear()}`;
  };

  const _handleAnalyticsDateRangeChange = (range) => {
    setAnalyticsDateRange(range);
    setSelectedFilters((prev) => ({
      ...prev,
      from_date: formatDate(range[0]),
      to_date: formatDate(range[1]),
    }));
  };

  // Reset filters
  const handleReset = () => {
    const cleared = {};
    Object.keys(filters).forEach((k) => (cleared[k.toLowerCase()] = null));
    cleared.from_date = "";
    cleared.to_date = "";

    setSelectedFilters(cleared);
    setAnalyticsDateRange([null, null]);
    setOpen(false);
  };

  const formatPlaceholder = (key) => {
    const withSpaces = key.replace(/_/g, " ");
    return withSpaces.charAt(0).toUpperCase() + withSpaces.slice(1);
  };

  return (
    <div className="e-filter-wrapper">
      {/* Toggle Button */}
      <div
        onClick={(e) => {
          e.stopPropagation();
          setOpen((prev) => !prev);
        }}
        className={`e-filter e-cursor-pointer e-border-1px-silver px-3 py-2 d-flex column-gap-2 justify-content-center align-items-center e-font-14 e-primary-font-poppins e-font-weight-500 ${
          open ? "e-text-blue-violet" : ""
        }`}
      >
        <Icon icon="filter-black" size="20px" />
        {open ? "Filter By" : "Filter"}
      </div>

      {/* Filter Body */}
      {open && (
        <div className="d-flex align-items-center e-filter-body mt-2 column-gap-3 py-0 position-absolute rounded-3 e-bg-magnolia z-3 px-3 text-nowrap">
          {/* Date Range */}
          {showDateRange && (
            <div className="d-flex align-items-center column-gap-2 ps-0 my-1">
              <DateRangePicker
                type={2}
                lableName={dateLabelName ? dateLabelName : "Joined Date"}
                value={analyticsDateRange}
                background="e-bg-white"
                ClassName="px-3"
                calenderPosition="e-dashboard-calender"
                onChange={_handleAnalyticsDateRangeChange}
              />
            </div>
          )}

          {/* Dynamic Filters */}
          {Object.keys(filters).map((key) => {
            const group = filters[key];
            if (!Array.isArray(group)) return null;

            const placeholder = formatPlaceholder(key);
            const selectedVal = selectedFilters[key];

            return (
              <div key={key}>
                <CheckboxSelect
                  isMulti={false}
                  placeholder={placeholder}
                  options={group}
                  value={group.find((g) => g.value === selectedVal) || null}
                  selectChange={(selected) =>
                    handleSelectChangeFor(key, selected)
                  }
                  isSearchable={false}
                  isClearable={true}
                  isDisabled={false}
                  error={null}
                  className="rounded-pill py-0"
                  type={2}
                  optional={false}
                />
              </div>
            );
          })}

          {/* Reset Button */}
          <div className="d-flex align-items-center column-gap-2 py-0">
            <PrimaryButton
              onPress={handleReset}
              size="16px"
              icon="reset"
              name="Reset Filter"
              ClassName="bg-transparent e-primary-font-poppins e-text-persian-red p-0 e-font-14 e-font-weight-400"
            />
          </div>
        </div>
      )}
    </div>
  );
}
